import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { searchAll } from "@/utils/nba";

interface SearchBoxProps {
  className?: string;
  autoFocus?: boolean;
  onNavigate?: () => void;
}

export default function SearchBox({ className = "", autoFocus = false, onNavigate }: SearchBoxProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef<HTMLDivElement>(null);

  const results = searchAll(query);
  const hasResults = results.teams.length > 0 || results.players.length > 0 || results.games.length > 0;

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const go = (path: string) => {
    setOpen(false);
    setQuery("");
    onNavigate?.();
    navigate(path);
  };

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 focus-within:border-primary-400 transition-colors">
        <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
          <i className="ri-search-line text-base" />
        </span>
        <input
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          autoFocus={autoFocus}
          type="text"
          placeholder="搜索球队 / 球员 / 赛事"
          className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            className="w-4 h-4 flex items-center justify-center text-foreground-400 hover:text-foreground-700 cursor-pointer"
            aria-label="清除搜索"
          >
            <i className="ri-close-line text-base" />
          </button>
        )}
      </div>

      {open && query.trim() && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-96 overflow-auto rounded-lg border border-background-200 bg-background-50 p-2">
          {!hasResults && (
            <p className="px-3 py-4 text-sm text-foreground-500 text-center">未找到与「{query}」相关的内容</p>
          )}

          {results.teams.length > 0 && (
            <div className="mb-1">
              <p className="px-3 py-1.5 text-xs font-semibold text-foreground-400">球队</p>
              {results.teams.slice(0, 5).map((team) => (
                <button
                  key={team.id}
                  type="button"
                  onClick={() => go(`/team/${team.id}`)}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-primary-50 cursor-pointer"
                >
                  <span
                    className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0"
                    style={{ backgroundColor: team.color }}
                  >
                    {team.abbreviation}
                  </span>
                  <span className="text-sm text-foreground-900">{team.fullName}</span>
                </button>
              ))}
            </div>
          )}

          {results.players.length > 0 && (
            <div className="mb-1">
              <p className="px-3 py-1.5 text-xs font-semibold text-foreground-400">球员</p>
              {results.players.slice(0, 5).map((player) => (
                <button
                  key={player.id}
                  type="button"
                  onClick={() => go(`/player/${player.id}`)}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-primary-50 cursor-pointer"
                >
                  <span className="w-7 h-7 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-800 shrink-0">
                    <i className="ri-user-line text-sm" />
                  </span>
                  <span className="text-sm text-foreground-900">
                    {player.name}
                    <span className="text-foreground-400"> · {player.position}</span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {results.games.length > 0 && (
            <div>
              <p className="px-3 py-1.5 text-xs font-semibold text-foreground-400">赛事</p>
              {results.games.map((game) => (
                <button
                  key={game.id}
                  type="button"
                  onClick={() => go(`/detail/${game.id}`)}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-primary-50 cursor-pointer"
                >
                  <span className="w-7 h-7 rounded-full bg-accent-100 flex items-center justify-center text-accent-700 shrink-0">
                    <i className="ri-basketball-line text-sm" />
                  </span>
                  <span className="text-sm text-foreground-900 truncate">
                    {game.homeTeamId.toUpperCase()} vs {game.awayTeamId.toUpperCase()} · {game.date}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}